/**
 * Cover generativa de un libro (compartida por biblioteca / player / mini-player).
 * Portada 1:1 de `claude-design/orborix-speak/v12/project/listen-screens.jsx`
 * (BookCover + CoverMotif).
 *
 * IMPORTANTE: las DIMENSIONES las da la CLASE que se le pasa (`ls-bookcard-cover`,
 * `bd-hero-cover`, `pl-cover`, `mp-cover` — definidas en listen.css), NO una prop
 * de tamaño. `pad` y `titleSize` ajustan el padding interno y la tipografía del
 * título según el contexto. Accent/motif/category salen de `deriveCoverMeta`
 * (backend si está, fallback determinístico si no).
 */

import { useState, type CSSProperties } from 'react';
import { coverImageUrl, deriveCoverMeta, type CoverMotif as Motif } from './coverMeta.js';
import type { CatalogBook } from './types.js';

export interface BookCoverProps {
  book: CatalogBook;
  className?: string;
  /** Padding interno en px (la cover chica usa menos). Default 12. */
  pad?: number;
  /** Tamaño del título en px. Default 19. */
  titleSize?: number;
  /**
   * Si renderizar el texto (kicker/título/autor) SOBRE la cover. Default true.
   * Poné `false` cuando el contexto ya muestra el título en otro lado (p. ej. la
   * card del rail lo repite debajo) para evitar texto duplicado y dejar el arte
   * limpio.
   */
  showText?: boolean;
  style?: CSSProperties;
}

const MOTIF_INK = 'rgba(12,13,21,0.5)';

function CoverMotif({ motif }: { motif: Motif }) {
  const dark = MOTIF_INK;
  switch (motif) {
    case 'orbit':
      return (
        <svg viewBox="0 0 100 130" preserveAspectRatio="xMidYMid slice" style={{ width: '100%', height: '100%' }}>
          {[20, 34, 48, 62].map((r) => (
            <circle key={r} cx="78" cy="20" r={r} fill="none" stroke={dark} strokeWidth="1.4" />
          ))}
          <circle cx="78" cy="20" r="5" fill={dark} />
        </svg>
      );
    case 'waves':
      return (
        <svg viewBox="0 0 100 130" preserveAspectRatio="none" style={{ width: '100%', height: '100%' }}>
          {[40, 56, 72, 88, 104].map((y) => (
            <path key={y} d={`M-5 ${y} Q 25 ${y - 14}, 50 ${y} T 105 ${y}`} fill="none" stroke={dark} strokeWidth="1.6" />
          ))}
        </svg>
      );
    case 'bloom':
      return (
        <svg viewBox="0 0 100 130" preserveAspectRatio="xMidYMid slice" style={{ width: '100%', height: '100%' }}>
          <g transform="translate(74,24)" fill="none" stroke={dark} strokeWidth="1.5">
            {[0, 45, 90, 135].map((a) => (
              <ellipse key={a} cx="0" cy="0" rx="30" ry="11" transform={`rotate(${a})`} />
            ))}
          </g>
        </svg>
      );
    case 'strata':
      return (
        <svg viewBox="0 0 100 130" preserveAspectRatio="none" style={{ width: '100%', height: '100%' }}>
          {[34, 46, 58, 70, 82, 94, 106].map((y, i) => (
            <line key={y} x1="-5" y1={y} x2="105" y2={y + (i % 2 ? 5 : -3)} stroke={dark} strokeWidth="1.5" />
          ))}
        </svg>
      );
    case 'split':
      return (
        <svg viewBox="0 0 100 130" preserveAspectRatio="none" style={{ width: '100%', height: '100%' }}>
          <path d="M0 0 L 100 60 L 100 0 Z" fill={dark} opacity="0.5" />
          <path d="M0 130 L 100 80 L 0 80 Z" fill={dark} opacity="0.3" />
        </svg>
      );
    case 'loop':
    default:
      return (
        <svg viewBox="0 0 100 130" preserveAspectRatio="xMidYMid slice" style={{ width: '100%', height: '100%' }}>
          {[0, 1, 2].map((i) => (
            <rect key={i} x={50 - i * 10} y={8 - i * 4} width={44 + i * 20} height={70 + i * 20} rx="22" fill="none" stroke={dark} strokeWidth="1.5" />
          ))}
        </svg>
      );
  }
}

export function BookCover({ book, className = '', pad = 12, titleSize, showText = true, style = {} }: BookCoverProps) {
  const meta = deriveCoverMeta(book);
  const a = meta.accent;
  const bg = `linear-gradient(155deg, ${a} 0%, ${a}d8 48%, ${a}9a 100%)`;
  const ts = titleSize ?? 19;

  // Cover ilustrada real (si existe para el slug). Si la imagen falla en cargar,
  // `imgOk=false` → cae al cover generativo (gradiente + motif) sin romper nada.
  const imgUrl = coverImageUrl(book.slug);
  const [imgOk, setImgOk] = useState(true);
  const hasImg = imgUrl !== null && imgOk;

  return (
    <div
      className={`bk-cover ${hasImg ? 'has-img' : ''} ${className}`}
      style={{ '--bk-pad': `${pad}px`, '--bk-accent': a, ...style } as CSSProperties}
    >
      {imgUrl && imgOk ? (
        <>
          <img className="bk-cover-img" src={imgUrl} alt="" loading="lazy" decoding="async" onError={() => setImgOk(false)} />
          <div className="bk-cover-scrim" />
        </>
      ) : (
        <>
          <div className="bk-cover-bg" style={{ background: bg }} />
          <div className="bk-cover-motif">
            <CoverMotif motif={meta.motif} />
          </div>
        </>
      )}
      {showText && (
        <div className="bk-cover-inner">
          <div className="bk-cover-kicker" style={{ fontSize: Math.max(7, ts * 0.42) }}>
            {meta.category.split(' ')[0]}
          </div>
          <div className="bk-cover-title" style={{ fontSize: ts }}>
            {book.title}
          </div>
          <div className="bk-cover-rule" />
          <div className="bk-cover-author" style={{ fontSize: Math.max(8, ts * 0.5) }}>
            {book.author}
          </div>
        </div>
      )}
    </div>
  );
}
